<script lang="ts" setup>
const {
  text,
  maxLength = 0,
  maxLines,
  ignoreColorStyles = false,
} = defineProps<{
  text: string;
  maxLength?: number;
  maxLines?: number;
  ignoreColorStyles?: boolean;
}>();

const isShowFullDescription = ref(false);
const contentElement = ref<HTMLElement | null>(null);
const isLineOverflowing = ref(false);
const isLineClampEnabled = computed(() => maxLines !== undefined);
const isShowExpandBtn = computed(() =>
  isLineClampEnabled.value ? isLineOverflowing.value : text.length > maxLength,
);
const shrunkText = computed(() => {
  if (text.length <= maxLength) return text;

  const lastChar = text[maxLength];

  return text.slice(0, maxLength + (lastChar === '>' ? 1 : lastChar === '<' ? -1 : 0)) + '...';
});
const contentStyle = computed(() => ({ '--folding-text-max-lines': maxLines }));

function updateLineOverflow() {
  if (!isLineClampEnabled.value || isShowFullDescription.value || !contentElement.value) return;

  isLineOverflowing.value = contentElement.value.scrollHeight > contentElement.value.clientHeight + 1;
}

watch(
  () => [text, maxLines],
  async () => {
    await nextTick();
    updateLineOverflow();
  },
);

watch(isShowFullDescription, async (isExpanded) => {
  if (isExpanded) return;

  await nextTick();
  updateLineOverflow();
});

onMounted(updateLineOverflow);
useResizeObserver(contentElement, updateLineOverflow);
</script>

<template>
  <div
    :class="{ 'folding-text-block--ignore-color-styles': ignoreColorStyles }"
    class="folding-text-block"
  >
    <template v-if="isLineClampEnabled">
      <div
        ref="contentElement"
        :class="{ 'folding-text-block__content--clamped': !isShowFullDescription }"
        :style="contentStyle"
        class="folding-text-block__content"
        v-html="text"
      />
    </template>

    <template v-else>
      <div
        v-if="!isShowFullDescription"
        v-html="shrunkText"
      />

      <FoldableBlock v-model="isShowFullDescription">
        <div v-html="text" />
      </FoldableBlock>
    </template>

    <button
      v-if="isShowExpandBtn"
      type="button"
      class="folding-text-block__expand-btn"
      @click="isShowFullDescription = !isShowFullDescription"
    >
      {{ isShowFullDescription ? 'Скрыть' : 'Развернуть' }}
    </button>
  </div>
</template>

<style lang="scss">
.folding-text-block {
  &--ignore-color-styles {
    [style*='color'],
    font[color] {
      color: inherit !important;
    }

    [style*='background'] {
      background-color: transparent !important;
    }
  }

  &__content--clamped {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--folding-text-max-lines);
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 16px;
    list-style: square;
    line-height: 1.2;
  }

  &__expand-btn {
    padding: 0;
    border: 0;
    background: none;
    color: $black-60;
    cursor: pointer;
    font: inherit;
    transition: all 0.2s;

    &:hover {
      color: $black-40;
      text-decoration: underline;
    }
  }
}
</style>
